import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';

const PROD_URL = 'https://dash.taliaz-cm.com';

// Every Cognito invite mail carries the dev address — the URL is baked into the pool's
// invite template at deploy time and one pool serves both environments, so a prod-only
// account follows the link and lands where its role is refused (Noam, 2026-10-26).
// Changing that template needs cognito-idp:UpdateUserPool, which nobody here holds, so
// the correction is on this side: send them on instead of making them read and click.
//
// Only a real "dev-" host redirects. localhost is a dev host too (env.ts), and bouncing
// a developer off their own machine mid-work would be worse than the wall it replaces.
const AUTO_SECONDS = 4;
function isDeployedDevHost(): boolean {
  return window.location.hostname.startsWith('dev-');
}

// Two different refusals used to render the same page, and it always said "you do not
// belong on dev, go to prod" (Ohad 2026-07-26 — that message sent him hunting for an
// environment problem when the real answer was a dashboard nobody had granted):
//   'env'   — this account may not be in the dev environment at all. Prod is the answer.
//   'grant' — the account is fine here; this particular dashboard was not given to it.
//             Sending that person to prod is wrong: the same wall is waiting there.
export type DeniedReason = 'env' | 'grant';

export default function AccessDenied({ reason = 'env' }: { reason?: DeniedReason }) {
  const { logout } = useAuth();
  const [copied, setCopied] = useState(false);
  const isEnv = reason === 'env';

  // The path travels with them: someone sent a deep link to a dashboard they do hold
  // arrives at that dashboard on prod, not at the front door.
  const target = PROD_URL + window.location.pathname + window.location.search;
  const autoSend = isEnv && isDeployedDevHost();
  const [left, setLeft] = useState(AUTO_SECONDS);

  useEffect(() => {
    if (!autoSend) return;
    const tick = setInterval(() => setLeft((s) => s - 1), 1000);
    const go = setTimeout(() => window.location.replace(target), AUTO_SECONDS * 1000);
    return () => { clearInterval(tick); clearTimeout(go); };
  }, [autoSend, target]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(PROD_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API blocked — fallback: select + manual copy
      window.prompt('העתק את הלינק:', PROD_URL);
    }
  };

  return (
    <div className="ad-wrap">
      <div className="ad-card">
        <div className="ad-icon">🔒</div>
        {isEnv ? (
          <>
            <h1 className="ad-title">גישה לסביבת פיתוח מוגבלת</h1>
            <p className="ad-text">
              הדשבורד הזה <code>dev-dash.taliaz-cm.com</code> הוא סביבת פיתוח. החשבון
              שלך לא מורשה לצפות בו.
            </p>
            <p className="ad-text">
              {autoSend
                ? <>מעבירים אותך לדשבורד הראשי בעוד <strong>{Math.max(left, 0)}</strong> שניות.</>
                : <>לצפייה בדשבורד הראשי <strong>השתמש בכתובת הבאה:</strong></>}
            </p>

            <div className="ad-link-row">
              <code className="ad-link-url">{PROD_URL}</code>
              <button className="ad-btn copy" onClick={copy}>
                {copied ? '✓ הועתק' : '📋 העתק'}
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="ad-title">אין לך הרשאה לדשבורד הזה</h1>
            <p className="ad-text">
              החשבון שלך תקין ואתה נמצא במקום הנכון — פשוט לא ניתנה לך גישה
              לדשבורד המסוים הזה.
            </p>
            <p className="ad-text">
              פנה לאוהד כדי לקבל אותה. בינתיים אפשר לחזור לדשבורדים שכן פתוחים לך.
            </p>
          </>
        )}

        <div className="ad-actions">
          {isEnv ? (
            <a className="ad-btn primary" href={target}>
              ← {autoSend ? 'קח אותי עכשיו' : 'העבר אותי לפרוד'}
            </a>
          ) : (
            <a className="ad-btn primary" href="/">
              ← חזרה לדשבורדים שלי
            </a>
          )}
          <button className="ad-btn" onClick={logout}>
            התנתק
          </button>
        </div>
      </div>

      <style>{`
        .ad-wrap {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #0f172a;
          font-family: 'Heebo', system-ui, sans-serif;
          direction: rtl;
          padding: 24px;
        }
        .ad-card {
          max-width: 480px;
          width: 100%;
          background: #1e293b;
          border: 1px solid #1f2a44;
          border-radius: 16px;
          padding: 32px 28px;
          text-align: center;
          color: #f1f5f9;
        }
        .ad-icon { font-size: 48px; margin-bottom: 16px; }
        .ad-title {
          font-size: 22px; font-weight: 700; margin: 0 0 16px 0;
          color: #fde047;
        }
        .ad-text {
          font-size: 14px; color: #cbd5e1; line-height: 1.6;
          margin: 0 0 12px 0;
        }
        .ad-text code {
          background: rgba(59,130,246,0.12); border: 1px solid rgba(59,130,246,0.3);
          padding: 1px 6px; border-radius: 4px; color: #93c5fd;
          font-family: 'JetBrains Mono', Consolas, monospace; font-size: 12px;
          direction: ltr; display: inline-block;
        }
        .ad-link-row {
          display: flex; gap: 8px; align-items: center;
          background: #0f172a; border: 1px solid #1f2a44;
          border-radius: 8px; padding: 8px 12px; margin: 16px 0;
        }
        .ad-link-url {
          flex: 1; direction: ltr; text-align: left;
          font-family: 'JetBrains Mono', Consolas, monospace; font-size: 13px;
          color: #93c5fd; word-break: break-all;
        }
        .ad-btn {
          background: #1f2a44; border: 1px solid #334155;
          color: #cbd5e1; padding: 8px 14px; border-radius: 6px;
          font-family: inherit; font-size: 13px; font-weight: 600;
          cursor: pointer; text-decoration: none; display: inline-block;
          transition: background 0.15s;
        }
        .ad-btn:hover { background: #334155; }
        .ad-btn.primary {
          background: rgba(59,130,246,0.2);
          border-color: rgba(59,130,246,0.5);
          color: #93c5fd;
        }
        .ad-btn.primary:hover { background: rgba(59,130,246,0.3); }
        .ad-btn.copy { white-space: nowrap; }
        .ad-actions {
          display: flex; gap: 8px; justify-content: center; margin-top: 20px;
        }
      `}</style>
    </div>
  );
}
